import { supabase } from '@/integrations/supabase/client';
import { Discount } from '@/components/company/types/planTypes';

// Type assertion to allow access to tables not defined in the database types
const supabaseClient = supabase as any;

/**
 * Toggles a discount's presence in a specific version
 * 
 * @param discountId The ID of the discount to toggle
 * @param slug The company slug
 * @param version The version to toggle the discount in
 * @param pageId The page ID associated with the discount
 * @returns Object with success status and whether the discount was added or removed
 */
export async function toggleDiscountInVersion(
  discountId: string,
  slug: string,
  version: string,
  pageId: string
): Promise<{ success: boolean; message: string; added?: boolean; removed?: boolean; error?: string }> {
  console.log('toggleDiscountInVersion called with:', { discountId, slug, version, pageId });
  if (!discountId || !slug || !version || !pageId) {
    console.error('Missing required parameters:', { 
      hasDiscountId: !!discountId, 
      hasSlug: !!slug, 
      hasVersion: !!version, 
      hasPageId: !!pageId 
    });
    return {
      success: false,
      message: 'Discount ID, company slug, version, and page ID are required',
      error: 'Missing required parameters'
    };
  }

  try {
    console.log(`🔄 [discountUtils] Toggling discount ${discountId} for ${slug}:${version}`);
    
    // Check if mapping already exists
    const { data: existingMapping, error: checkError } = await supabaseClient
      .from('discounts_map')
      .select('id')
      .eq('discount_id', discountId)
      .eq('slug', slug)
      .eq('version', version)
      .maybeSingle();
    
    if (checkError) {
      console.error('❌ [discountUtils] Error checking discounts_map:', checkError);
      return {
        success: false,
        message: 'Failed to check if discount mapping exists',
        error: checkError.message
      };
    }
    
    // If mapping exists, remove it
    if (existingMapping) {
      const { error: deleteError } = await supabaseClient
        .from('discounts_map')
        .delete()
        .eq('id', existingMapping.id);
      
      if (deleteError) {
        console.error('❌ [discountUtils] Error removing discount mapping:', deleteError);
        return {
          success: false,
          message: 'Failed to remove discount from version',
          error: deleteError.message
        };
      }
      
      console.log(`✅ [discountUtils] Removed discount ${discountId} from version ${version}`);
      
      return {
        success: true,
        message: 'Successfully removed discount from version',
        removed: true
      };
    } 
    // Otherwise, add a new mapping
    else {
      const { error: insertError } = await supabaseClient
        .from('discounts_map')
        .insert({
          discount_id: discountId,
          slug,
          version,
          page_id: pageId
        });
      
      if (insertError) {
        console.error('❌ [discountUtils] Error adding discount mapping:', insertError);
        return {
          success: false,
          message: 'Failed to add discount to version',
          error: insertError.message
        };
      }
      
      console.log(`✅ [discountUtils] Added discount ${discountId} to version ${version}`);
      
      return {
        success: true,
        message: 'Successfully added discount to version',
        added: true
      };
    }
  } catch (error) {
    console.error('❌ [discountUtils] Unexpected error in toggleDiscountInVersion:', error);
    return {
      success: false,
      message: 'An unexpected error occurred',
      error: error.message
    };
  }
}

/**
 * Removes a discount and all its mappings from the database
 * 
 * @param discountId The ID of the discount to remove
 * @param slug The company slug
 * @returns Object with success status and message
 */
export async function removeDiscount(
  discountId: string,
  slug: string
): Promise<{ success: boolean; message: string; error?: string }> {
  if (!discountId || !slug) {
    return {
      success: false,
      message: 'Discount ID and company slug are required',
      error: 'Missing required parameters'
    };
  }

  try {
    console.log(`🔄 [discountUtils] Deleting discount ${discountId} for slug ${slug}`);
    
    // First, check for existing entries in discounts_map
    const { data: existingMappings, error: checkError } = await supabaseClient
      .from('discounts_map')
      .select('id, discount_id, slug, version')
      .eq('discount_id', discountId);
    
    if (checkError) {
      console.error('❌ [discountUtils] Error checking discounts_map:', checkError);
      return {
        success: false,
        message: 'Failed to check discount mappings',
        error: checkError.message
      };
    }
    
    console.log(`ℹ️ [discountUtils] Found ${existingMappings?.length || 0} mappings for discount ${discountId}`);
    
    // Step 1: Delete entries from discounts_map
    const { error: mapDeleteError } = await supabaseClient
      .from('discounts_map')
      .delete()
      .eq('discount_id', discountId);
    
    if (mapDeleteError) {
      console.error('❌ [discountUtils] Error deleting from discounts_map:', mapDeleteError);
      return {
        success: false,
        message: 'Failed to delete discount mappings',
        error: mapDeleteError.message
      };
    }
    
    console.log(`✅ [discountUtils] Deleted mappings for discount ${discountId}`);
    
    // Step 2: Delete the discount itself
    const { error: discountDeleteError } = await supabaseClient
      .from('discounts')
      .delete()
      .eq('id', discountId);
    
    if (discountDeleteError) {
      console.error('❌ [discountUtils] Error deleting discount:', discountDeleteError);
      return {
        success: false,
        message: 'Failed to delete discount',
        error: discountDeleteError.message
      };
    }
    
    console.log(`✅ [discountUtils] Deleted discount ${discountId}`);
    
    return {
      success: true,
      message: `Successfully deleted discount ${discountId} and ${existingMappings?.length || 0} mappings`
    };
  } catch (error) {
    console.error('❌ [discountUtils] Unexpected error in removeDiscount:', error);
    return {
      success: false,
      message: 'An unexpected error occurred',
      error: error.message
    };
  }
}
